import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { RewardsV1Service } from '../services/rewardsV1Service';
import { githubLightTheme, JsonEditor, type JsonData } from 'json-edit-react';

const RewardsV1Detail: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [serverData, setServerData] = useState<unknown>(null);
  const [editorData, setEditorData] = useState<JsonData | null>(null);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const draftKey = id ? `rewardsv1:draft:${id}` : null;

  const lockedTopLevelKeys = useMemo(() => {
    return new Set(['_id', 'bank_name', 'card_name', 'createdAt', 'cardId', 'updatedAt']);
  }, []);

  const restrictEdit = useMemo(() => {
    return ({ path, level }: any) => {
      // Prevent "edit full object as JSON" at the root, to avoid bypassing locks.
      if (level === 0) return true;
      const topLevelKey = Array.isArray(path) && path.length === 1 ? path[0] : null;
      return typeof topLevelKey === 'string' && lockedTopLevelKeys.has(topLevelKey);
    };
  }, [lockedTopLevelKeys]);

  const restrictDelete = useMemo(() => {
    return ({ path }: any) => {
      const topLevelKey = Array.isArray(path) && path.length === 1 ? path[0] : null;
      return typeof topLevelKey === 'string' && lockedTopLevelKeys.has(topLevelKey);
    };
  }, [lockedTopLevelKeys]);

  const displayData = editorData ?? serverData;

  const canUseEditor = useMemo(() => {
    // json-edit-react requires object|array data
    return editorData !== null && typeof editorData === 'object';
  }, [editorData]);

  const prettyJson = useMemo(() => {
    try {
      return JSON.stringify(displayData, null, 2);
    } catch {
      return String(displayData);
    }
  }, [displayData]);

  useEffect(() => {
    document.title = id ? `RewardsV1 • ${id}` : 'RewardsV1';
  }, [id, draftKey]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      if (!id) {
        setError('Missing id');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setSaveError(null);
        setSaveMessage(null);
        const res = await RewardsV1Service.getById(id);
        if (!isMounted) return;
        setServerData(res.data);

        const serverIsObject = res.data !== null && typeof res.data === 'object';
        const serverEditorData = serverIsObject ? (res.data as JsonData) : null;

        // If a draft exists locally, prefer it for editor state.
        if (draftKey) {
          try {
            const raw = localStorage.getItem(draftKey);
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed !== null && typeof parsed === 'object') {
                setEditorData(parsed as JsonData);
                setSaveMessage('Loaded locally saved draft');
              } else {
                setEditorData(serverEditorData);
              }
            } else {
              setEditorData(serverEditorData);
            }
          } catch {
            setEditorData(serverEditorData);
          }
        } else {
          setEditorData(serverEditorData);
        }
      } catch (e) {
        if (!isMounted) return;
        setError(e instanceof Error ? e.message : 'Failed to load RewardsV1 document');
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [id, draftKey]);

  const saveEdits = async () => {
    if (!id) return;
    if (editorData === null || typeof editorData !== 'object') return;

    setSaveLoading(true);
    setSaveError(null);
    setSaveMessage(null);

    try {
      // Placeholder endpoint for now; swap backend later without changing UI.
      const res = await RewardsV1Service.updateById(id, editorData);
      setServerData(res.data);
      if (draftKey) {
        try {
          localStorage.removeItem(draftKey);
        } catch {
          // ignore
        }
      }
      setSaveMessage('Saved');
    } catch (e) {
      // Fallback: persist draft locally so the user can refresh without losing edits.
      if (draftKey) {
        try {
          localStorage.setItem(draftKey, JSON.stringify(editorData));
          setSaveMessage('Saved locally (mock)');
          return;
        } catch {
          // ignore
        }
      }
      setSaveError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaveLoading(false);
    }
  };

  const resetEditor = () => {
    if (serverData !== null && typeof serverData === 'object') {
      setEditorData(serverData as JsonData);
      if (draftKey) {
        try {
          localStorage.removeItem(draftKey);
        } catch {
          // ignore
        }
      }
      setSaveError(null);
      setSaveMessage(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">RewardsV1 Detail</h1>
          <p className="mt-1 text-sm text-gray-500 break-all">{id}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/rewards"
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Back
          </Link>
          {canUseEditor ? (
            <button
              onClick={saveEdits}
              disabled={!editorData || saveLoading}
              className="inline-flex items-center px-4 py-2 border border-blue-600 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {saveLoading ? 'Saving…' : 'Save'}
            </button>
          ) : null}
          {canUseEditor ? (
            <button
              onClick={resetEditor}
              disabled={!serverData}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Reset
            </button>
          ) : null}
        </div>
      </div>

      {saveError ? <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-3 text-sm">{saveError}</div> : null}
      {saveMessage ? <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded-md p-3 text-sm">{saveMessage}</div> : null}

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          <p className="mt-2 text-gray-500">Loading RewardsV1 document…</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{error}</div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-4">
            {canUseEditor ? (
              <div>
                <div className="bg-gray-50 border border-gray-200 rounded p-3 overflow-auto">
                  <JsonEditor
                    data={editorData as JsonData}
                    setData={setEditorData}
                    theme={githubLightTheme}
                    viewOnly={false}
                    collapse={2}
                    enableClipboard
                    restrictEdit={restrictEdit}
                    restrictDelete={restrictDelete}
                  />
                </div>
              </div>
            ) : (
              <pre className="text-xs whitespace-pre-wrap break-words bg-gray-50 border border-gray-200 rounded p-3">{prettyJson}</pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RewardsV1Detail;
